import { Stack, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import { BookingPartial, ExtraOption } from '../../redux/ellipsisApi';
import { formatCurrency } from '../../theme/theme';
import {
  formatDateDisplay as formatDate,
  subtractDaysUTC,
} from '../../utils/dates/date-formatter';
import AdditionalItems from '../common/AdditionalItems';

interface OwnProps {
  booking: BookingPartial | null | undefined;
  vatRegistered?: boolean;
  currencyCode: string;
  bookableCurrencyCode?: string;
}

interface ExtraOptionWithQuantity extends ExtraOption {
  qty: number;
}
const BookingCost: React.FC<OwnProps> = (props) => {
  const { booking, currencyCode, vatRegistered, bookableCurrencyCode } = props;

  const includedExtras = useMemo(() => {
    try {
      return (
        ((booking?.extraOptions as ExtraOption[])
          ?.filter((option) => option?.payment === 'online')
          .reduce((newArray: ExtraOptionWithQuantity[], option) => {
            if (!option) return newArray;
            const index = newArray.findIndex((n) => n.id === option.id);
            if (index < 0) {
              newArray.push({ ...option, qty: 1 });
            } else {
              const existingOption = newArray[index];
              newArray.splice(index, 1, {
                ...existingOption,
                qty: existingOption.qty + 1,
              });
            }
            return newArray;
          }, []) as ExtraOption[]) ?? []
      );
    } catch (error) {
      console.error('Error processing included extras:', error);
      return [];
    }
  }, [booking?.extraOptions]);

  const excludedExtras = useMemo(() => {
    try {
      return (
        ((booking?.extraOptions as ExtraOption[])
          ?.filter((option: any) => option?.payment !== 'online')
          .reduce((newArray: ExtraOptionWithQuantity[], option) => {
            if (!option) return newArray;
            const index = newArray.findIndex((n) => n.id === option.id);
            if (index < 0) {
              newArray.push({ ...option, qty: 1 });
            } else {
              const existingOption = newArray[index];
              newArray.splice(index, 1, {
                ...existingOption,
                qty: existingOption.qty + 1,
              });
            }
            return newArray;
          }, []) as ExtraOption[]) ?? []
      );
    } catch (error) {
      console.error('Error processing excluded extras:', error);
      return [];
    }
  }, [booking?.extraOptions]);

  const rentalPrice = useMemo(() => {
    try {
      if (!booking) {
        return formatCurrency(0, currencyCode);
      }

      let qty = 1;
      if (booking.bookableType === 'Boat') {
        qty = booking.fullDays ?? 0;
      } else if (booking.bookableType === 'Property') {
        qty = booking.fullDays && booking.fullDays > 0 ? booking.fullDays : 0;
      } else if (booking.bookableType === 'Vehicle') {
        qty = booking.fullDays ?? 0;
      }

      const rate = booking.rate ?? 0;
      const bookingCurrencyCode = booking.currencyCode ?? currencyCode;

      return formatCurrency(rate * qty, bookingCurrencyCode);
    } catch (error) {
      console.error('Error calculating rental price:', error);
      return formatCurrency(0, currencyCode);
    }
  }, [booking, currencyCode]);

  try {
    if (!booking) {
      return (
        <Stack direction="column" spacing={4}>
          <Typography>Booking information not available</Typography>
        </Stack>
      );
    }

    return (
      <Stack direction="column" spacing={4}>
        <Stack direction="column" spacing={2}>
          <Typography fontWeight="bold">Hire</Typography>
          {!booking.offer ? (
            <Stack direction="row" spacing={1}>
              <Typography>Rental:</Typography>
              <Typography> {rentalPrice} </Typography>
            </Stack>
          ) : (
            <Stack direction="column" spacing={0.5}>
              <Stack direction="row" spacing={1}>
                <Typography>Rental:</Typography>
                <Typography
                  sx={{ textDecoration: 'line-through', opacity: 0.8 }}
                >
                  {rentalPrice}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Typography fontWeight="bold">Offer price:</Typography>
                <Typography fontWeight="bold">
                  {formatCurrency(
                    (booking.bookingTotal ?? 0) - (booking.serviceCharge ?? 0),
                    booking.currencyCode ?? currencyCode
                  )}
                </Typography>
              </Stack>
            </Stack>
          )}
          {vatRegistered && !!booking.bookingTotal && (
            <Stack direction="row" spacing={1}>
              <Typography>VAT @ 15%:</Typography>
              <Typography>
                {' '}
                {formatCurrency(
                  (((booking.rate ?? 0) * (booking.fullDays ?? 0)) / 1.15) *
                    0.15,
                  currencyCode
                )}{' '}
              </Typography>
            </Stack>
          )}
        </Stack>

        {includedExtras.length > 0 && (
          <Stack direction="column" spacing={2}>
            <Stack direction="column" spacing={1}>
              <Typography fontWeight="bold">
                Additional Items included
              </Typography>
              <AdditionalItems
                booking={booking}
                bookableCurrencyCode={bookableCurrencyCode}
                allExtras={includedExtras}
              />
            </Stack>
          </Stack>
        )}

        <Stack direction="column" spacing={2}>
          <Stack direction="column" spacing={2}>
            {!!booking.serviceCharge && (
              <Stack direction="row" spacing={1}>
                <Typography fontWeight="bold">Service charge:</Typography>
                <Typography fontWeight="bold">
                  {' '}
                  {formatCurrency(
                    booking.serviceCharge,
                    booking.currencyCode ?? currencyCode
                  )}{' '}
                </Typography>
              </Stack>
            )}
          </Stack>

          {(!!booking.selectedPaymentPlan ||
            !!booking.selectedMonthlyInstallments) && (
            <Stack direction="row" spacing={1}>
              <Typography>Pay</Typography>
              <Typography fontWeight="bold">
                {formatCurrency(
                  booking.initialPrice ?? 0,
                  booking.currencyCode ?? currencyCode
                )}
              </Typography>
              <Typography>today</Typography>
            </Stack>
          )}
          {!!booking.selectedPaymentPlan && booking.startDate && (
            <Stack direction="row" spacing={1}>
              <Typography>Pay</Typography>
              <Typography fontWeight="bold">
                {formatCurrency(
                  (booking.bookingTotal ?? 0) - (booking.initialPrice ?? 0),
                  booking.currencyCode ?? currencyCode
                )}
              </Typography>
              <Typography>
                on{' '}
                {formatDate(subtractDaysUTC(booking.startDate, 3), false)}{' '}
              </Typography>
            </Stack>
          )}
          {!!booking.selectedMonthlyInstallments &&
            !!booking.numberOfInstallments && (
              <Stack direction="row" spacing={1}>
                <Typography>Pay</Typography>
                <Typography fontWeight="bold">
                  {formatCurrency(
                    booking.installmentAmount ?? 0,
                    booking.currencyCode ?? currencyCode
                  )}
                </Typography>
                <Typography>
                  per month for {booking.numberOfInstallments} months
                </Typography>
              </Stack>
            )}
          <Stack direction="row" spacing={1}>
            <Typography fontWeight="bold" sx={{ fontSize: '20px' }}>
              Total
            </Typography>
            <Typography fontWeight="bold" sx={{ fontSize: '20px' }}>
              {booking.status === 'in-progress'
                ? 'Payment due now:'
                : 'Payment due:'}
            </Typography>
            <Typography
              fontWeight="bold"
              sx={{ fontSize: '20px' }}
              data-cy={'booking-total'}
            >
              {formatCurrency(
                booking.bookingTotal ?? 0,
                booking.currencyCode ?? currencyCode
              )}
            </Typography>
          </Stack>

          {!!booking.nonDiscountedRate && (
            <Stack direction="row" spacing={1}>
              <Typography
                fontWeight="bold"
                sx={{ fontSize: '18px', color: 'red' }}
              >
                Price before discount:
              </Typography>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: '18px',
                  textDecoration: 'line-through',
                  color: 'red',
                }}
                data-cy={'booking-total'}
              >
                {formatCurrency(
                  booking.nonDiscountedRate,
                  booking.currencyCode ?? currencyCode
                )}
              </Typography>
            </Stack>
          )}
          {excludedExtras.length > 0 && (
            <Stack direction="column" spacing={1}>
              <Typography fontWeight="bold">
                Pay directly to owner later
              </Typography>
              <AdditionalItems
                booking={booking}
                bookableCurrencyCode={bookableCurrencyCode}
                allExtras={excludedExtras}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  } catch (error) {
    console.error('Error rendering booking cost:', error);
    return (
      <Stack direction="column" spacing={4}>
        <Typography>
          An error occurred while loading booking cost information
        </Typography>
      </Stack>
    );
  }
};

export default BookingCost;
